<script setup>
import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store.js';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  to: { type: [Object, String], default: '' },
  label: { type: String, default: '' },
  icon: { type: [String, Object], default: '' },
  expandable: { type: Boolean, default: false },
  isExpanded: { type: Boolean, default: false },
  isActive: { type: Boolean, default: false },
  hasActiveChild: { type: Boolean, default: false },
  getterKeys: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['toggle']);

const showBadge = useMapGetter(props.getterKeys.badge);
const dynamicCount = useMapGetter(props.getterKeys.count);
const count = computed(() =>
  dynamicCount.value > 99 ? '99+' : dynamicCount.value
);
</script>

<template>
  <component
    :is="to ? 'router-link' : 'div'"
    class="flex h-10 min-w-0 items-center gap-2 rounded-2xl border border-transparent px-3 py-1.5 transition-colors duration-150"
    role="button"
    draggable="false"
    :to="to"
    :title="label"
    :class="{
      'border-[rgb(var(--whaisper-shell-border))] bg-[rgb(var(--whaisper-shell-panel-muted))] font-medium text-[rgb(var(--whaisper-shell-heading))] shadow-sm':
        isActive && !hasActiveChild,
      'bg-[rgb(var(--whaisper-shell-accent-soft)/0.75)] font-medium text-[rgb(var(--whaisper-shell-heading))]':
        hasActiveChild,
      'text-[rgb(var(--whaisper-shell-copy))] hover:border-[rgb(var(--whaisper-shell-border))] hover:bg-white/70':
        !isActive && !hasActiveChild,
    }"
    @click.stop="emit('toggle')"
  >
    <div v-if="icon" class="relative flex items-center gap-2">
      <Icon v-if="icon" :icon="icon" class="size-4" />
      <span
        v-if="showBadge"
        class="size-2 -top-px ltr:-right-px rtl:-left-px bg-n-brand absolute rounded-full border border-n-solid-2"
      />
    </div>
    <div class="flex items-center gap-1.5 flex-grow min-w-0 flex-1">
      <span
        class="truncate"
        :class="{
          'font-medium text-sm text-[rgb(var(--whaisper-shell-heading))]':
            isActive || hasActiveChild,
          'text-sm text-[rgb(var(--whaisper-shell-copy))]':
            !isActive && !hasActiveChild,
        }"
      >
        {{ label }}
      </span>
      <span
        v-if="dynamicCount && !expandable"
        class="flex-shrink-0 rounded-full border px-2 py-0.5 text-center text-[11px] font-semibold leading-4 capitalize"
        :class="{
          'border-[rgb(var(--whaisper-shell-border-strong))] text-[rgb(var(--whaisper-shell-heading))]':
            isActive || hasActiveChild,
          'border-[rgb(var(--whaisper-shell-border))] text-[rgb(var(--whaisper-shell-copy))]':
            !isActive && !hasActiveChild,
        }"
      >
        {{ count }}
      </span>
    </div>
    <span
      v-if="expandable"
      v-show="isExpanded"
      class="i-lucide-chevron-up size-3 text-[rgb(var(--whaisper-shell-copy))]"
      @click.stop="emit('toggle')"
    />
  </component>
</template>
